import { z } from 'zod';

/**
 * O consumo de tokens de cada chamada ao modelo no Codex, sintetizado como
 * evento (ADR-0021).
 *
 * O app-server conta o uso em `thread/tokenUsage/updated`, e até aqui o daemon
 * descartava a notificação: nenhum token do Codex chegava ao banco, e o rateio
 * da cota semanal por sessão não teria o que ratear. O plano do Codex dizia
 * para descartar (`agent-data/plans/codex/PLANO.md`); o ADR-0021 reverte.
 *
 * **Não é conversa, e por isso não tem `message`.** Os três extratores de texto
 * (`textoDoEvento`, a função SQL `remoto_texto_do_evento` e o `extracao.py` do
 * cérebro) leem `message.content` e devolvem vazio para o resto — um número de
 * tokens nunca aparece na busca. O caso `codexUsoDoMotor` de
 * `fixturas-texto.json` prende isso nos três lados.
 *
 * `input_tokens` é o total de entrada INCLUINDO o cache, que é a convenção da
 * OpenAI (no Claude é o contrário: `input_tokens` exclui o cache). Quem desconta
 * é o peso (`pesoDoUso`), nunca esta carga: ela guarda o número como o motor
 * contou.
 */

/** O `tipo` do `EventoSessao`. Nome novo no eixo `tipo`, que é `z.string()`: sem versão de protocolo. */
export const TIPO_USO_DO_MOTOR = 'uso-do-motor';

/**
 * O sufixo do `uuidTurno` deste evento: `<turnId>:u`.
 *
 * **Um evento por CHAMADA ao modelo, não por turno** — M0-23
 * (`agent-data/plans/codex/MEDICOES.md`) mediu uma notificação por chamada: o
 * turno com uma ferramenta no meio teve duas. A primeira chamada do turno usa
 * `<turnId>:u` (o turno de uma chamada só fica exatamente assim) e as
 * seguintes `<turnId>:u2`, `<turnId>:u3`… (`apps/agent/src/codex/uso-do-motor.ts`).
 * Com um uuid só por turno, o gravador — que descarta duplicata em silêncio —
 * guardaria a primeira e perderia as outras. O sufixo também impede a colisão
 * com a fala (`<id>`) do mesmo item.
 */
export const SUFIXO_USO_DO_MOTOR = ':u';

const Contagem = z.number().int().nonnegative();

export const UsoDoCodexSchema = z.object({
  input_tokens: Contagem,
  cached_input_tokens: Contagem,
  output_tokens: Contagem,
  reasoning_output_tokens: Contagem,
});
export type UsoDoCodex = z.infer<typeof UsoDoCodexSchema>;

export const CargaUsoDoMotorSchema = z.object({
  type: z.literal(TIPO_USO_DO_MOTOR),
  /** O modelo que atendeu, quando o app-server diz. Nunca adivinhado. */
  model: z.string().min(1).max(200).nullable(),
  usage: UsoDoCodexSchema,
  /** A janela de contexto do modelo, como o motor a relata. É o limite do cabeçalho da sessão. */
  contextWindow: z.number().int().positive().nullable(),
  turnId: z.string().min(1),
  /**
   * O custo REAL da chamada em US$, como o motor o relatou (F16, Interface
   * congelada item 12). Só o OPENCODE o manda — o `info.cost` da mensagem
   * (OC-23). Opcional e SEM `.default()`: ausente é "o motor não disse", nunca
   * zero; o Codex não reporta dinheiro, e um zero aqui viraria "o Codex é de
   * graça" no total da semana.
   */
  custoUsd: z.number().nonnegative().optional(),
});
export type CargaUsoDoMotor = z.infer<typeof CargaUsoDoMotorSchema>;

/** O `uuidTurno` do evento de uso daquele turno. */
export function uuidDoUsoDoMotor(turnId: string): string {
  return `${turnId}${SUFIXO_USO_DO_MOTOR}`;
}

/**
 * Monta a carga. O `type` interno repete o `tipo` do evento, como todas as outras.
 *
 * **Não valida nem lança**: quem lê o cru do app-server confere com
 * `UsoDoCodexSchema.safeParse` antes de chamar — o daemon não pode cair por
 * uma notificação de telemetria estranha. Copia só os quatro campos, para que
 * um campo novo do motor não viaje até o banco sem ninguém ter decidido.
 */
export function cargaDeUsoDoMotor(entrada: {
  model: string | null;
  usage: UsoDoCodex;
  contextWindow: number | null;
  turnId: string;
  /** Só o OPENCODE manda (F16). Ausente não vira campo nenhum na carga. */
  custoUsd?: number;
}): CargaUsoDoMotor {
  return {
    type: TIPO_USO_DO_MOTOR,
    model: entrada.model,
    usage: {
      input_tokens: entrada.usage.input_tokens,
      cached_input_tokens: entrada.usage.cached_input_tokens,
      output_tokens: entrada.usage.output_tokens,
      reasoning_output_tokens: entrada.usage.reasoning_output_tokens,
    },
    contextWindow: entrada.contextWindow,
    turnId: entrada.turnId,
    ...(entrada.custoUsd !== undefined ? { custoUsd: entrada.custoUsd } : {}),
  };
}
